<template>
  <ASiteContainer>
    <p class="text-primary text-center mb-2 text-[1.1rem] font-bold">
      {{ $t("pages.becomePartner.headline") }}
    </p>

    <h1 class="text-center first-line:text-3xl font-black">
      {{ $t("pages.becomePartner.title") }}
    </h1>

    <p class="mt-4 sm:px-24 text-center">
      {{ $t("pages.becomePartner.description") }}
    </p>

    <section
      class="bg-gradient-to-r from-transparent via-gray-100/80 to-transparent rounded-2xl py-8 px-4 my-8"
    >
      <h2 class="text-xl font-bold text-primary text-center mb-2">
        {{ $t("pages.becomePartner.readyToJoin.headline") }}
      </h2>
      <h3 class="text-2xl font-bold text-center mb-5">
        {{ $t("pages.becomePartner.joinUs.title") }}
      </h3>
      <div
        class="flex flex-col sm:flex-row items-center justify-center sm:space-x-8"
      >
        <UButton
          :to="
            externalUrl('agency', {
              name: 'register',
            })
          "
          color="brand"
          :size="$device.isMobile ? 'xl' : '2xl'"
          class="w-full rounded-full justify-center sm:w-auto"
          trailing-icon="mdi:arrow-right"
        >
          {{ $t("pages.becomePartner.readyToJoin.button") }}
        </UButton>
      </div>
    </section>

    <section class="mt-8">
      <h2 class="text-2xl font-bold text-center mb-8">
        {{ $t("pages.becomePartner.advantages.title") }}
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <ASpotlightCard
          v-for="(advantage, index) in advantages"
          :key="index"
          :from="advantage.from"
          :via="advantage.via"
          :size="300"
          class="w-full max-w-2xl shadow-lg shadow-gray-100"
        >
          <div class="flex flex-col items-center">
            <UIcon :name="advantage.icon" class="w-12 h-12 mb-4 text-primary" />
            <h3 class="text-[1.15rem] font-bold mb-4 text-center">
              {{ $t(`pages.becomePartner.advantages.${advantage.key}.title`) }}
            </h3>
            <p class="text-center">
              {{
                $t(
                  `pages.becomePartner.advantages.${advantage.key}.description`,
                )
              }}
            </p>
          </div>
        </ASpotlightCard>
      </div>
    </section>

    <AImg
      src="/images/banner-partner.jpg"
      class="w-full h-[500px] mt-12 object-cover shadow-2xl rounded-2xl bg-top object-top"
    />

    <section class="mt-16">
      <h2 class="text-2xl font-bold text-center mb-8">
        {{ $t("pages.becomePartner.ourKeyFigures.title") }}
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <ASpotlightCard
          class="flex flex-col items-start py-6 px-12 bg-white ring-1 ring-offset-4 ring-primary"
          mode="after"
          from="rgba(153,207,255,0.2)"
          :size="300"
          background-classes="bg-blue-50/50"
        >
          <div class="text-4xl font-bold text-primary mb-2">100+</div>
          <h3 class="text-xl font-semibold mb-2">
            {{ $t("pages.becomePartner.ourKeyFigures.destinations.title") }}
          </h3>
          <p class="text-left">
            {{
              $t("pages.becomePartner.ourKeyFigures.destinations.description")
            }}
          </p>
        </ASpotlightCard>
        <ASpotlightCard
          class="flex flex-col items-start py-6 px-12 bg-white ring-1 ring-offset-4 ring-primary"
          mode="after"
          from="rgba(153,207,255,0.2)"
          :size="300"
          background-classes="bg-blue-50/50"
        >
          <div class="text-4xl font-bold text-primary mb-2">
            {{ decimal(100000) }}+
          </div>
          <h3 class="text-xl font-semibold mb-2">
            {{ $t("pages.becomePartner.ourKeyFigures.travelers.title") }}
          </h3>
          <p class="text-left">
            {{ $t("pages.becomePartner.ourKeyFigures.travelers.description") }}
          </p>
        </ASpotlightCard>
        <ASpotlightCard
          class="flex flex-col items-start py-6 px-12 bg-white ring-1 ring-offset-4 ring-primary"
          mode="after"
          from="rgba(153,207,255,0.2)"
          :size="300"
          background-classes="bg-blue-50/50"
        >
          <div class="text-4xl font-bold text-primary mb-2">95%</div>
          <h3 class="text-xl font-semibold mb-2">
            {{ $t("pages.becomePartner.ourKeyFigures.satisfactions.title") }}
          </h3>
          <p class="text-left">
            {{
              $t("pages.becomePartner.ourKeyFigures.satisfactions.description")
            }}
          </p>
        </ASpotlightCard>
      </div>
    </section>

    <section class="mt-16">
      <h2 class="text-2xl font-bold text-center mb-6">
        {{ $t("pages.becomePartner.readyToJoin.title") }}
      </h2>

      <div
        class="flex flex-col sm:flex-row items-center justify-center sm:space-x-8"
      >
        <UButton
          :to="
            externalUrl('agency', {
              name: 'register',
            })
          "
          color="brand"
          :size="$device.isMobile ? 'xl' : '2xl'"
          class="w-full rounded-full justify-center sm:w-auto"
          trailing-icon="mdi:arrow-right"
        >
          {{ $t("pages.becomePartner.readyToJoin.button") }}
        </UButton>
      </div>
    </section>
  </ASiteContainer>
</template>

<script lang="ts" setup>
const { t } = useI18n()

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/werden-partner',
    en: '/become-partner',
    es: '/ser-parte',
    fr: '/devenir-partenaire',
    it: '/diventare-partner',
    nl: '/worden-partner',
    pt: '/ser-parte',
  },
})

const advantages = [
  {
    description: t(
      'pages.becomePartner.advantages.higherVisibility.description',
    ),
    from: '#1cd1c6',
    icon: 'mdi:eye-outline',
    key: 'higherVisibility',
    title: t('pages.becomePartner.advantages.higherVisibility.title'),
    via: '#407cff',
  },
  {
    description: t(
      'pages.becomePartner.advantages.increaseInIncome.description',
    ),
    from: '#1cd1c6',
    icon: 'mdi:chart-bar',
    key: 'increaseInIncome',
    title: t('pages.becomePartner.advantages.increaseInIncome.title'),
    via: '#407cff',
  },
  {
    description: t(
      'pages.becomePartner.advantages.innovativeTools.description',
    ),
    from: '#1cd1c6',
    icon: 'mdi:tools',
    key: 'innovativeTools',
    title: t('pages.becomePartner.advantages.innovativeTools.title'),
    via: '#407cff',
  },
  {
    description: t(
      'pages.becomePartner.advantages.notorietyOfThePetitFute.description',
    ),
    from: '#1cd1c6',
    icon: 'mdi:star-outline',
    key: 'notorietyOfThePetitFute',
    title: t('pages.becomePartner.advantages.notorietyOfThePetitFute.title'),
    via: '#407cff',
  },
  {
    description: t(
      'pages.becomePartner.advantages.trainingAndSupport.description',
    ),
    from: '#1cd1c6',
    icon: 'mdi:book-open-outline',
    key: 'trainingAndSupport',
    title: t('pages.becomePartner.advantages.trainingAndSupport.title'),
    via: '#407cff',
  },
  {
    description: t(
      'pages.becomePartner.advantages.creationInStays.description',
    ),
    from: '#1cd1c6',
    icon: 'mdi:office-building-outline',
    key: 'creationInStays',
    title: t('pages.becomePartner.advantages.creationInStays.title'),
    via: '#407cff',
  },
]

useSeoMeta({
  description: t('pages.becomePartner.metaDescription'),
  ogDescription: t('pages.becomePartner.metaDescription'),
  ogTitle: t('pages.becomePartner.metaTitle'),
  title: t('pages.becomePartner.metaTitle'),
})
</script>

<style scoped>
.bg-primary {
  background-color: #4a90e2;
}
</style>
